import { Alert, Button, Col, Form, FormInstance, Input, InputNumber, message, Row, Select, Radio, Card, DatePicker, Tag, Space } from "antd"
import React, { useEffect, useRef, useState } from "react"
import dayjs from "dayjs"
import { formatInputNumber, parseInputNumber, roundValue, textNumberFormater } from "../../utils"
import { useConfig } from "../../contexts/ConfigContext"
import { modules } from "../../config/pages"
import { useStore } from "../../contexts/StoreContext"
import { useRegister } from "../../contexts/RegisterContext"
import { useStoreFetch } from "../../hooks/useInventory"
import { useCompany } from "../../contexts/CompanyContext"
import useAuth from "../../hooks/useAuth"
import { useUser } from "../../contexts/UserContext"
import ValidatedEntityInput from "../ValidatedEntityInput"
import { MinusCircleOutlined } from "@ant-design/icons"
import MovementAttachmentUploader from "../common/MovementAttachmentUploader"
import { useSecureRequest } from "../../hooks/useSecureRequest"

type formProps = {
  form: FormInstance
}

function RegisterMovementEntryForm({form}:Readonly<formProps>){
  const {config} = useConfig()
  const {store,updateStore} = useStore()
  const {company} = useCompany()
  const {user} = useUser()
  const {register} = useRegister()
  const idCounter = useRef(-1);
  const getNextId = () => --idCounter.current;
  const [payments, setPayments] = useState([{id:-1,key:"CASH"}]);
  const [enableAdd, setEnableAdd] = useState(false);
  const [payWarning,setPayWarning] = useState(false)
  const [lockDate, setLockDate] = useState<string | null>(null);
  const { securePost } = useSecureRequest();

  useEffect(() => {
    if (company.current?.id || company.current?.uid) {
      securePost("/accounting/period-lock", {
        company_id: company.current?.id,
        company: company.current?.uid,
      })
        .then((res) => {
          const lk = res?.data?.lock?.lock_date || res?.data?.lock_date;
          if (lk) {
            setLockDate(String(lk).substring(0, 10));
          } else {
            setLockDate(null);
          }
        })
        .catch(() => null);
    }
  }, [company.current?.id, company.current?.uid]);
  
  const {
    pendingListEntries,
    pendingListExits,
    currentPending,
  } = register
  const {tokenData} = useAuth.useToken('authCsrfToken',config.session,config.key)
  const {data} = useStoreFetch(tokenData,{
    all:true,
    company:company.current.uid
  })
  const mod = config.module

  const approverRequiredOption = company.moduleOptions.find(opt => opt.type === 'MOVEMENT_ENTRY_CONFIG' && opt.key === 'APPROVER_REQUIRED');
  const approverRequired = approverRequiredOption ? approverRequiredOption.numeric_value === 1 : false;

  const total = (currentPending?.total_amount_net) || 0

  const recalculateAmounts = (targetTotalPaid?: number) => {
    const formValues = form.getFieldsValue()
    let totalPaid = 0
    if (targetTotalPaid !== undefined) {
      totalPaid = targetTotalPaid
    } else {
      for (const key in formValues) {
        if (key.startsWith("amount_") && formValues[key] !== undefined && formValues[key] !== null) {
          totalPaid += Number(formValues[key])
        }
      }
    }

    const currentMode = form.getFieldValue("execution_mode") || "IMMEDIATE"

    if (currentMode === "DEFERRED") {
      // Factura Diferida (Causación):
      // Saldo pendiente estimado que queda por pagar al proveedor
      const pendingBalance = Math.max(0, total - totalPaid)
      form.setFieldsValue({ cash_return_amount: roundValue(pendingBalance) })
      setEnableAdd(totalPaid < total)
    } else {
      // Compra Inmediata (Contado):
      // Vueltas / cambio a devolver al pagador (total pagado - total factura)
      const totalReturn = totalPaid - total
      form.setFieldsValue({ cash_return_amount: roundValue(totalReturn) })
      setEnableAdd(totalPaid < total)
    }
  }

  useEffect(()=>{
    const isDeferredCredit = (currentPending?.execution_mode === "DEFERRED" && (currentPending?.payment_term || "CREDIT") === "CREDIT")
    const initialPaid = isDeferredCredit ? 0 : ((currentPending?.total_amount_net) || 0)
    const initialReturn = isDeferredCredit 
      ? ((currentPending?.total_amount_net) || 0)
      : (currentPending?.cash_return_amount || 0)

    form.setFieldsValue({
      execution_mode: currentPending?.execution_mode || "IMMEDIATE",
      payment_term: currentPending?.payment_term || "CREDIT",
      document_date: currentPending?.document_date ? dayjs(currentPending.document_date) : dayjs().startOf('minute'),
      total_amount_net:(currentPending?.total_amount_net) || 0,
      cash_return_amount: initialReturn,
      ['amount_-1']: initialPaid,
      ['pay_method_-1']: 'CASH',
      attachments: currentPending?.attachments || []
    })
    if(data?.storehouse){
      updateStore({list: data.storehouse})
    }
  },[data,register,form,mod,pendingListEntries,pendingListExits])

  useEffect(() => {
    if (!approverRequired && user) {
      form.setFieldsValue({
        approved_by_user_id: user.id || 0,
        approved_by_user_name: user.name || "",
        approved_by_user_document: user.document_number || "",
        received_by_user_id: user.id || 0,
        received_by_user_name: user.name || "",
        received_by_user_document: user.document_number || "",
        delivered_by_user_id: user.id || 0,
        delivered_by_user_name: user.name || "",
        delivered_by_user_document: user.document_number || ""
      });
    }
  }, [approverRequired, user, form]);
  
  const handleEntityChange = (field: string, data: { id: number; name: string; document: string }) => {
    form.setFieldsValue({
      [`${field}_id`]: data.id,
      [`${field}_name`]: data.name,
      [`${field}_document`]: data.document
    });
  };
  
  const removePay = (id:number) => {
    if(id > 0){
      message.error("No se puede eliminar un impuesto que ya se encuentra registrado")
      return
    }
    const filterResult = payments.filter(item => item.id !== id)
    const result = filterResult.length ? filterResult : [{ id: -1,key:"CASH"}]
    if(result[0].id === -1 && result.length === 1 && payments.length === 1){
      form.setFieldsValue({[`pay_method_${result[0].id}`]: "CASH"})
    }
    setPayments(result)
    const formValues = form.getFieldsValue()
    let totalPaid = 0
    for(const key in formValues){
      if(key.startsWith("amount_") && formValues[key] !== undefined && formValues[key] !== null && !key.includes(id.toString())){
        totalPaid += Number(formValues[key])
      }
    } 
    recalculateAmounts(totalPaid)
  }

  const changeReceived = (_value:number | null) => {
    recalculateAmounts()
  }

  const scanForEmptyFields = (data:any,setData:any,setShoWarning:any,key:string)=>{
    const values = form.getFieldsValue()
    console.log("Values: ",values)
    let emptyValues = false
    for(const str in values){
      emptyValues = str.startsWith(key) && values[str] === undefined
      if(emptyValues) {
        break
      }
    }
    if(emptyValues){
      setShoWarning(true)
    }else{
      const id = getNextId()
      setData([...data, { id: id,key:"CASH" }]);
      form.setFieldsValue({[`pay_method_${id}`]: "CASH"})
      setShoWarning(false)
    }
  }
  const onChangePayMethod = (value:any,id:number) => {
    const payArray = [...payments]
    const index = payArray.findIndex(item => item.id === id)
    if(index === -1){
      return
    }
    payArray[index].key = value
    setPayments(payArray)
  }
  const movementType = Form.useWatch("document_type",form)
  const executionMode = Form.useWatch("execution_mode", form) || "IMMEDIATE"

  const items = currentPending?.item_list || []
  const tangibleItems = items.filter((el: any) => el.track_inventory !== false && el.register_type !== 'SERVICE')
  const serviceItems = items.filter((el: any) => el.track_inventory === false || el.register_type === 'SERVICE')
  const tangibleTotal = tangibleItems.reduce((acc: number, el: any) => acc + Number(el.total_amount_net || el.total_amount_full || 0), 0)
  const serviceTotal = serviceItems.reduce((acc: number, el: any) => acc + Number(el.total_amount_net || el.total_amount_full || 0), 0)

  return(
  <>
    <Row gutter={[10, 10]} align="top">
      <Form.Item name="approved_by_user_id" hidden><Input type="hidden"/></Form.Item>
      <Form.Item name="approved_by_user_name" hidden><Input type="hidden"/></Form.Item>
      <Form.Item name="received_by_user_id" hidden><Input type="hidden"/></Form.Item>
      <Form.Item name="received_by_user_name" hidden><Input type="hidden"/></Form.Item>
      <Form.Item name="received_by_user_document" hidden><Input type="hidden"/></Form.Item>
      <Form.Item name="contact_id" hidden><Input type="hidden"/></Form.Item>
      <Form.Item name="contact_name" hidden><Input type="hidden"/></Form.Item>
      <Form.Item name="delivered_by_user_id" hidden><Input type="hidden"/></Form.Item>
      <Form.Item name="delivered_by_user_name" hidden><Input type="hidden"/></Form.Item>
      <Form.Item name="delivered_by_user_document" hidden><Input type="hidden"/></Form.Item>
      {movementType === "PURCHASE_RECEIPT" && <>
      {serviceItems.length > 0 && (
        <Col xs={24}>
          <div style={{ padding: "8px 12px", backgroundColor: "#f0f5ff", border: "1px solid #adc6ff", borderRadius: 8, marginBottom: 12, fontSize: 12 }}>
            <span style={{ fontWeight: 600, color: "#1d39c4" }}>📋 Composición de la compra: </span>
            {tangibleItems.length > 0 && (
              <Tag color="cyan" style={{ marginLeft: 4 }}>
                📦 {tangibleItems.length} ítem(s) de inventario ({textNumberFormater(tangibleTotal)})
              </Tag>
            )}
            <Tag color="geekblue" style={{ marginLeft: 4 }}>
              🚚 {serviceItems.length} servicio(s) / costos asociados ({textNumberFormater(serviceTotal)})
            </Tag>
          </div>
        </Col>
      )}
      <Col xs={24}>
        <Card size="small" style={{ marginBottom: 12, backgroundColor: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 8 }}>
          <Row gutter={[16, 8]} align="middle">
            <Col xs={24} md={14}>
              <Form.Item
                label="Flujo de Registro"
                name="execution_mode"
                initialValue="IMMEDIATE"
                style={{ marginBottom: 0 }}
                tooltip="Inmediato: Carga el inventario al instante y procesa el pago. Diferido (Causación): Radica la factura contable; la entrada física a bodega y los pagos/abonos se gestionan de forma independiente."
              >
                <Radio.Group 
                  buttonStyle="solid"
                  onChange={(e) => {
                    const newMode = e.target.value;
                    if (newMode === "DEFERRED") {
                      const currentTerm = form.getFieldValue("payment_term") || "CREDIT";
                      if (currentTerm === "CREDIT") {
                        form.setFieldsValue({ ['amount_-1']: 0 });
                        recalculateAmounts(0);
                      } else {
                        form.setFieldsValue({ ['amount_-1']: total });
                        recalculateAmounts(total);
                      }
                    } else {
                      form.setFieldsValue({ ['amount_-1']: total });
                      recalculateAmounts(total);
                    }
                  }}
                >
                  <Radio.Button value="IMMEDIATE">⚡ Compra Inmediata</Radio.Button>
                  <Radio.Button value="DEFERRED">📋 Factura Diferida (Causación)</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            {executionMode === "DEFERRED" && (
              <Col xs={24} md={10}>
                <Form.Item
                  label="Condición de Pago"
                  name="payment_term"
                  initialValue="CREDIT"
                  style={{ marginBottom: 0 }}
                >
                  <Select 
                    style={{ width: '100%' }}
                    onChange={(val) => {
                      if (val === "CREDIT") {
                        form.setFieldsValue({ ['amount_-1']: 0 });
                        recalculateAmounts(0);
                      } else {
                        form.setFieldsValue({ ['amount_-1']: total });
                        recalculateAmounts(total);
                      }
                    }}
                  >
                    <Select.Option value="CREDIT">Crédito (Abonos posteriores)</Select.Option>
                    <Select.Option value="CASH">Contado / Anticipo inicial</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>
      </Col>
      <Col xs={24} sm={{span:12}}>
        <ValidatedEntityInput
          type="PROVIDER"
          label="Origen/proveedor"
          value={currentPending?.contact_document}
          initialName={currentPending?.contact_name}
          initialId={currentPending?.contact_id}
          rules={[{ required: true, message: 'Proveedor requerido' }]}
          name="contact_document"
          onChange={(data) => handleEntityChange('contact', data)}
        />
      </Col>
      <Col xs={24} sm={{span:12}}>
        <Form.Item
          label="Nombre contacto"
          name={`contact_name`}
          rules={[{ required: true, message: 'Ingrese un nombre'}]}
          
        >
          <Input maxLength={50} placeholder="Nombre contacto" style={{ width: '100%' }}></Input>
        </Form.Item>
      </Col>
      </>}
      {approverRequired && <>
        <Col xs={24} sm={{span:12}}>
          <ValidatedEntityInput
            type="USER"
            label="Aprobador"
            value={currentPending?.approved_by_user_document}
            initialName={currentPending?.approved_by_user_name}
            initialId={currentPending?.approved_by_user_id}
            rules={[{ required: true, message: 'Aprobador requerido' }]}
            name="approved_by_user_document"
            onChange={(data) => handleEntityChange('approved_by_user', data)}
          />
        </Col>
        <Col xs={24} sm={{span:12}}>
          <ValidatedEntityInput
            type="USER"
            label="Recibido por"
            value={currentPending?.received_by_user_document}
            initialName={currentPending?.received_by_user_name}
            initialId={currentPending?.received_by_user_id}
            rules={[{ required: true, message: 'Se requiere un usuario' }]}
            name="received_by_user_document"
            onChange={(data) => handleEntityChange('received_by_user', data)}
          />
        </Col>
      </>}
      
      <Col xs={24} sm={{span:12}}>
        <Form.Item
          label="Detalle de orden"
          name={`notes`}
          rules={[{ required: true, message: 'Se requiere una nota'}]}
          
        >
          <Input placeholder="Comentarios adicionales" style={{ width: '100%' }}></Input>
        </Form.Item>
      </Col>
      <Col xs={24} sm={{span:12}}>
        <Form.Item
          label="Soporte de orden"
          name={`reference_document`}
          rules={[{ required: true, message: 'Se requiere un soporte'}]}
        >
          <Input placeholder="No de Factura/Orden" style={{ width: '100%' }}></Input>
        </Form.Item>
      </Col>
      <Col xs={24} sm={{span:12}}>
        <Form.Item
          label={
            <Space size={6} align="center">
              <span>Fecha de factura / documento</span>
              {lockDate && (
                <Tag color="error" bordered={false} style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                  Cerrado: {dayjs(lockDate).format('DD/MM/YYYY')}
                </Tag>
              )}
            </Space>
          }
          name="document_date"
          tooltip={lockDate ? `Periodo contable cerrado hasta el ${dayjs(lockDate).format('DD/MM/YYYY')}. Solo se permiten registros con fecha posterior.` : undefined}
          rules={[
            { required: true, message: 'Seleccione fecha del documento' },
            {
              validator: (_, value) => {
                if (value && lockDate) {
                  const valDay = dayjs(value);
                  const lockDay = dayjs(lockDate);
                  if (valDay.isBefore(lockDay, 'day') || valDay.isSame(lockDay, 'day')) {
                    return Promise.reject(new Error(`El periodo contable hasta el ${lockDay.format('DD/MM/YYYY')} se encuentra cerrado.`));
                  }
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <DatePicker 
            style={{ width: '100%' }} 
            format="YYYY-MM-DD" 
            placeholder="Fecha de la factura"
            disabledDate={(current) => {
              if (!lockDate || !current) return false;
              return current.isBefore(dayjs(lockDate), 'day') || current.isSame(dayjs(lockDate), 'day');
            }}
          />
        </Form.Item>
      </Col>
      <Col xs={24} sm={{span:12}}>
        <Form.Item
          label="Total factura"
          name={`total_amount_net`}
          rules={[{ required: true, message: ''}]}
          
        >
          <InputNumber 
            disabled prefix="$" min={1}
            formatter={formatInputNumber}
            parser={parseInputNumber}
            placeholder="Valor facturado" 
            style={{ width: '100%' }}>
          </InputNumber>
        </Form.Item>
      </Col>

      {movementType === "PURCHASE_RECEIPT" && <Col xs={24} sm={{span:12}}>
        <Form.Item
          label={
            <Space size={6} align="center">
              <span>{mod === modules.RegisterExit ? "Almacén origen" : "Almacén destino"}</span>
              {tangibleItems.length === 0 && serviceItems.length > 0 && (
                <Tag color="default" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                  Referencia contable
                </Tag>
              )}
            </Space>
          }
          name={mod === modules.RegisterExit ? `storehouse_from_id`:`storehouse_to_id`}
          rules={[{ required: true, message: 'Seleccione un almacén'}]}
          tooltip={
            tangibleItems.length === 0 && serviceItems.length > 0
              ? "Esta compra solo incluye servicios/gastos directos (no ingresa stock físico a bodega). El almacén se asigna como centro de referencia."
              : serviceItems.length > 0
              ? "La mercancía física ingresará al almacén seleccionado. Los servicios se registrarán como costos/gastos directos de la compra."
              : undefined
          }
        >
          <Select style={{width:"100%"}}>
            {store.list.map(el => <Select.Option key={el.id} value={el.id}>{el.name}</Select.Option>)}
          </Select>
        </Form.Item>
      </Col>}
      {movementType !== "PURCHASE_RECEIPT" && (
        <>
          {approverRequired && (
            <Col xs={24} sm={{span:12}}>
              <ValidatedEntityInput
                type="USER"
                label="Entregado por"
                value={currentPending?.delivered_by_user_document}
                initialName={currentPending?.delivered_by_user_name}
                initialId={currentPending?.delivered_by_user_id}
                rules={[{ required: true, message: 'Se requiere un usuario' }]}
                name="delivered_by_user_document"
                onChange={(data) => handleEntityChange('delivered_by_user', data)}
              />
            </Col>
          )}
          <Col xs={24} sm={{span:12}}>
            <Form.Item
              label={
                <Space size={6} align="center">
                  <span>{['PURCHASE_ORDER', 'PURCHASE_REQUEST'].includes(movementType) ? "Almacén previsto de entrega" : "Almacén destino"}</span>
                  {['PURCHASE_ORDER', 'PURCHASE_REQUEST'].includes(movementType) && (
                    <Tag color="default" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                      Referencia operativa
                    </Tag>
                  )}
                </Space>
              }
              name={`storehouse_to_id`}
              rules={[{ required: true, message: 'Seleccione un almacén'}]}
              tooltip={
                ['PURCHASE_ORDER', 'PURCHASE_REQUEST'].includes(movementType)
                  ? "Indica la bodega donde el proveedor entregará el pedido. Al ser documento preparatorio, no sumará stock físico hasta la recepción de compra."
                  : undefined
              }
            >
              <Select style={{width:"100%"}}>
                {store.list.map(el => <Select.Option key={el.id} value={el.id}>{el.name}</Select.Option>)}
              </Select>
            </Form.Item>
          </Col>
        </>
      )}
    </Row>
    {movementType === "PURCHASE_RECEIPT" && <>
      <h5 style={{marginLeft:"8.3333%",marginBottom:"0.5em",fontWeight:500,fontSize:"1rem"}}>Datos de pago</h5>
      {payments.map((pay,index) => (
        <Row key={pay.id} gutter={[10,10]} align="middle">
          <Col xs={24} sm={{span:12}}>
            <Form.Item
              label= {index === 0 ? "Medio de pago":""}
              name={`pay_method_${pay.id}`}
              rules={[{ required: true, message: 'Selecciona el medio pago' }]}
            >
            <Select placeholder="Selecciona el medio pago" onChange={(value) => onChangePayMethod(value,pay.id)} >
              {register.paymentOptions.map(el => 
                <Select.Option key={el.id} value={el.key}>{el.string_value}</Select.Option>
              )}
            </Select>
            </Form.Item>
          </Col>
          <Col xs={20} sm={{span:10}}>
            <Form.Item
              label={index === 0 ? "Total pagado" : ""}
              name={`amount_${pay.id}`}
              rules={[{ required: true,message:"" }]}>
              <InputNumber 
                prefix="$" min={0} 
                style={{width:"100%"}} 
                formatter={formatInputNumber}
                parser={parseInputNumber}
                onChange={(e) => changeReceived(e)}>
              </InputNumber>
            </Form.Item>
          </Col>
          <Col span={2} style={{marginTop: index === 0 ? "16px":"-12px"}}>
          {
            payments.length > 1 ? (
              <MinusCircleOutlined 
                onClick={() => removePay(pay.id)}
              />
            ) : null
          }
          </Col>
          {form.getFieldValue(`pay_method_${pay.id}`) !== "CASH" && <Col xs={24} sm={{span:12}}>
            <Form.Item
              label= "Referencia"
              name={`reference_${pay.id}`}
              rules={[{ required: true, message: 'Selecciona el medio pago' }]}
            >
              <Input placeholder="Referencia" style={{ width: '100%', marginBottom: "1em" }}></Input>
            </Form.Item>
          </Col>}
        </Row>
      ))}
      <Row gutter={[20,10]}>
        <Col xs={24} sm={{span:12}}>
          {payWarning && <Alert
            message="Complete los campos antes de añadir otro pago"
            banner
            closable
            onClose={()=>setPayWarning(false)}
          />}
        </Col>
      </Row>
      {enableAdd && <Row gutter={[20,10]} style={{marginBottom:5}}>
        <Col xs={8} sm={{span:5}}>
          <Button type="dashed" style={{marginTop:"5px"}} onClick={() => scanForEmptyFields(payments,setPayments,setPayWarning,'amount_')}>Agregar pago</Button>
        </Col>
      </Row>}
      <Row gutter={[10,10]}> 
        <Col sm={12} xs={{span:12}}>
          <Form.Item
            label={executionMode === "DEFERRED" ? "Saldo pendiente estimado" : "Total por cubrir/devolver"}
            name="cash_return_amount"
            rules={executionMode === "DEFERRED" ? [] : [{ required: true,message:"Valide el total de la venta" }]}>
            <InputNumber 
              prefix="$" min={0} 
              style={{width:"100%"}} 
              formatter={formatInputNumber}
              parser={parseInputNumber}
              disabled>
            </InputNumber>
          </Form.Item>
        </Col>
      </Row>
    </>}
    <Row gutter={[10, 10]} style={{ marginTop: 12 }}>
      <Col span={24}>
        <Form.Item name="attachments" label={null} style={{ marginBottom: 0 }}>
          <MovementAttachmentUploader
            movementId={currentPending?.id}
            movementUid={currentPending?.uid}
            defaultDocType={movementType}
          />
        </Form.Item>
      </Col>
    </Row>
  </>
  )
}

export default RegisterMovementEntryForm